<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch, type ComponentPublicInstance } from "vue";
import { useRoute, useRouter } from "vue-router";
import { VOnboardingWrapper, VOnboardingStep, useVOnboarding, type VOnboardingWrapperOptions } from "v-onboarding";
import * as Sentry from "@sentry/vue";

import { useFlowGuideCoordinationStore } from "@/stores/flowGuide/flowGuideCoordination.store";
import { useWelcomeFlowStore } from "@/stores/welcomeFlow/welcomeFlow.store";
import { placementForElement, spotlightOverlay } from "@/components/FlowGuide/flowGuidePlacement";
import { updateFlowGuideSeenStatus } from "@/api/services/FlowGuide";

import WelcomeConfetti from "./WelcomeConfetti.vue";
import WelcomeTourCard from "./WelcomeTourCard.vue";
import { useWelcomeStepMarkdown } from "./useWelcomeStepMarkdown.composable";
import { orderStepsForDeck, type WelcomeDeck } from "./decks";
import { heldMenuOffsetModifiers, type PopperOffsetModifier } from "./heldMenuOffset";
import type { GuideStep } from "./loadProjectOverviewGuideSteps";
import type { WelcomeStepCta, WelcomeStepReached, WelcomeVariant } from "./types/WelcomeFlowTypes";

const props = withDefaults(
  defineProps<{
    deck: WelcomeDeck;
    variant: WelcomeVariant;
    // An on-demand replay: Escape closes it whatever the deck. A real run can be left only when its
    // deck is not `mandatory` (see canDismiss / showsSkip below).
    dismissible?: boolean;
    // Whether this run opened right after the start dialog created a project — forwarded to the
    // deck's ending.ctaLabel (see welcomeFlow.store.ts's ActiveWelcomeTour.afterCreation).
    afterCreation?: boolean;
    // Whether this run opened via openTour(..., { onDemand: true }) — e.g. the Materials guide's ⓘ
    // — forwarded to the deck's ending.ctaLabel so a deck whose CTA opens a real write flow (the
    // materials create deck) only offers it on an auto run, never on demand.
    onDemand?: boolean;
    // Dependency injection for stories/tests — defaults to the deck's own loader, resolved at call
    // time (not a Vue prop default: Function-typed defaults aren't treated as factories).
    loadSteps?: () => Promise<GuideStep[]>;
  }>(),
  { dismissible: false, afterCreation: false, onDemand: false }
);

const emit = defineEmits<{
  finished: [result: "completed" | "skipped", stepReached: WelcomeStepReached | null];
}>();

type TourStepEntity = {
  key: string;
  id: string;
  title: string;
  bodyHtml: string;
  alreadySeen: boolean;
  cta?: WelcomeStepCta;
  holdsOpenMenu?: string;
  // Whether THIS step highlights its anchor (data-welcome-tour-anchor, set in checkIfElementIsFound)
  // — carried onto the built step since checkIfElementIsFound only ever sees this, not the source GuideStep.
  highlight: boolean;
  attachTo: { element: string; classList: string[] };
  options: {
    popper: { placement: ReturnType<typeof placementForElement>; modifiers?: PopperOffsetModifier[] };
    overlay?: { borderRadius: number };
  };
};

// The deck only supplies the large centered intro directly — the anchored steps that follow come
// live from its own loadSteps (below), not from static per-deck data. An on-demand replay (a nav
// ⓘ) skips it and goes straight to the anchored steps.
const introWelcomeStep = props.onDemand ? null : props.deck.intro?.[props.variant] ?? null;

// Two gates, deliberately different. Escape (canDismiss): any dismissible run, or a real run of a
// non-mandatory deck. The Skip button (showsSkip): an on-demand replay, or a non-mandatory deck — but
// never a dismissible preview of the mandatory platform deck, so a preview shows what a real run
// shows. Only the platform tour is mandatory: one moment everyone should see once. A skipped real run
// is logged as abandoned with the card reached, which is the data behind that decision.
const canDismiss = computed(() => props.dismissible || !props.deck.mandatory);
const showsSkip = computed(() => props.onDemand || !props.deck.mandatory);

const steps = ref<TourStepEntity[]>([]);
const showingIntro = ref(false);
const totalStepCount = computed(() => steps.value.length + (introWelcomeStep ? 1 : 0));
// With an intro, the anchored deck's own index 0 displays as step 2 — the intro is step 1.
const displayIndexFor = (index: number) => (introWelcomeStep ? index + 1 : index);

const onboardingWrapper = ref(null);
const { start, goToStep, finish } = useVOnboarding(onboardingWrapper);
const { loadStep } = useWelcomeStepMarkdown();
const coordinationStore = useFlowGuideCoordinationStore();
const welcomeFlowStore = useWelcomeFlowStore();
const router = useRouter();
const route = useRoute();

// Same value used below by spotlightOverlay — the cut-out is drawn this many px outside the anchor,
// so the radius has to grow by the same amount to still meet its edges cleanly.
const OVERLAY_PADDING = 0;

// Both decks run with a real spotlight cut-out over a dimmed page, matching the ⓘ flow guide.
const onboardingOptions: VOnboardingWrapperOptions = {
  overlay: { enabled: true, padding: OVERLAY_PADDING, borderRadius: 8 },
  scrollToStep: { enabled: true, options: { behavior: "auto", block: "nearest", inline: "nearest" } },
  popper: {
    modifiers: [
      { name: "offset", options: { offset: [0, 12] } },
      { name: "preventOverflow", options: { mainAxis: false, altAxis: true, padding: 8 } },
      // No flip — placementForElement already picks the side with room, per-step, at start() time.
      { name: "flip", options: { fallbackPlacements: [] } },
    ],
  },
};

const overlayOptionsFor = (anchorId: string): { overlay?: { padding: number; borderRadius: number } } => {
  const overlay = spotlightOverlay(anchorId, OVERLAY_PADDING);
  return overlay === null ? {} : { overlay };
};

// Guide steps come from the same Carbon0 rows as the page's own ⓘ flow guide, already sorted by the
// deck's own loadSteps. Turned into tour steps here, at start() time, so
// placementForElement/spotlightOverlay measure the real, laid-out DOM. The deck's own "come back any
// time" replay card ends the tour, with the deck's ending row just before it (orderStepsForDeck) —
// on an on-demand replay the replay card is dropped and the ending row is last instead. The deck's
// CTA rides on whichever card is last, but is still asked of the ENDING row: if that row's usual
// anchor is missing but a fallback is present (e.g. no projects yet, so the empty state's own button
// takes over from the toolbar's), it retargets onto that and its CTA replaces a plain "Finish".
const buildAnchoredSteps = (guideSteps: GuideStep[]): TourStepEntity[] => {
  const { anchorId: endingAnchorId, fallbackAnchorId } = props.deck.ending;

  // The ending row's fallback takes over only when its own anchor is missing AND the fallback is
  // present — never when the primary anchor is there too, and never dropped as absent before the
  // fallback gets a chance (this must run BEFORE the presence filter below, not after it).
  const resolveAnchor = (step: GuideStep): { anchorId: string; isEndingFallback: boolean } => {
    const isEnding = step.anchorId === endingAnchorId;
    const primaryPresent = !!document.getElementById(step.anchorId);
    const isEndingFallback = isEnding && !primaryPresent && !!fallbackAnchorId && !!document.getElementById(fallbackAnchorId);
    return { anchorId: isEndingFallback && fallbackAnchorId ? fallbackAnchorId : step.anchorId, isEndingFallback };
  };

  // An on-demand replay (the nav ⓘ) already started from the very button a firstRunOnly step (e.g.
  // the deck's own "come back any time" card) points at — showing it again would be pointless. A
  // preview from the dev menu is dismissible but never onDemand, so the owner still sees it there.
  const eligibleSteps = props.onDemand ? guideSteps.filter((step) => !step.firstRunOnly) : guideSteps;
  const present = eligibleSteps.filter((step) => !!document.getElementById(resolveAnchor(step).anchorId));
  const orderedSteps = orderStepsForDeck(present, props.deck);

  // The CTA sits on the LAST card — the replay card on an auto run, the ending row on an on-demand
  // replay — but the label is still the ENDING row's own question: only that row can retarget onto a
  // fallback anchor (resolveAnchor above), and the label asks whether it did. No ending row survived
  // the presence filter means no CTA at all, so the last card reads a plain "Finish".
  const endingStep = orderedSteps.find((step) => step.anchorId === endingAnchorId);
  const endingCtaLabel = endingStep
    ? props.deck.ending.ctaLabel({
        afterCreation: props.afterCreation,
        fallbackUsed: resolveAnchor(endingStep).isEndingFallback,
        onDemand: props.onDemand,
      })
    : undefined;

  return orderedSteps.map((step, index) => {
    const { anchorId } = resolveAnchor(step);
    const isLastStep = index === orderedSteps.length - 1;
    // Measured now, at start() time, alongside placementForElement/spotlightOverlay above — the real,
    // laid-out DOM is what a held-open menu's own overhang has to clear.
    const anchorWidth = document.getElementById(anchorId)?.getBoundingClientRect().width ?? 0;
    const modifiers = heldMenuOffsetModifiers(anchorWidth, step.heldMenuWidth);

    return {
      key: step.key,
      id: step.id,
      title: step.title,
      bodyHtml: step.bodyHtml,
      alreadySeen: step.alreadySeen,
      cta: isLastStep && endingCtaLabel ? { label: endingCtaLabel } : undefined,
      holdsOpenMenu: step.holdsOpenMenu,
      highlight: step.highlight,
      // No class here any more — checkIfElementIsFound tags the anchor with an attribute instead (see
      // its own comment), which survives a re-render that patches this element's `:class` binding.
      attachTo: { element: `#${anchorId}`, classList: [] },
      options: {
        popper: { placement: placementForElement(anchorId), ...(modifiers.length > 0 ? { modifiers } : {}) },
        ...overlayOptionsFor(anchorId),
      },
    };
  });
};

// Fetched once, in parallel with the intro's own first paint — cached so returning to the intro
// (handleBack) and clicking Next again doesn't re-request it.
let guideStepsPromise: Promise<GuideStep[]> | null = null;
const fetchGuideSteps = (): Promise<GuideStep[]> => {
  guideStepsPromise ??= (props.loadSteps ?? props.deck.loadSteps)().catch((error: unknown) => {
    Sentry.captureException(error);
    return [];
  });
  return guideStepsPromise;
};

const introContent = ref({ title: "", bodyHtml: "" });
const preloadIntroContent = async () => {
  if (!introWelcomeStep) return;
  introContent.value = await loadStep(props.variant, introWelcomeStep.key);
};

// Popper measures the card before it is visible; a resize after mount makes it re-measure.
let lastRepositionedIndex = -1;
const repositionStepCard = (index: number) => {
  if (index === lastRepositionedIndex) return;
  lastRepositionedIndex = index;
  Promise.resolve().then(() => window.dispatchEvent(new Event("resize")));
};

let cardWrapperEl: HTMLElement | null = null;
let lastFocusedIndex = -1;
const focusCard = (index: number) => {
  if (index === lastFocusedIndex) return;
  lastFocusedIndex = index;
  cardWrapperEl?.focus();
};

// Bound to a plain <div> below, so this is always an Element, never a component instance.
const setCardWrapperEl = (el: Element | ComponentPublicInstance | null) => {
  cardWrapperEl = el instanceof HTMLElement ? el : null;
};

let introWrapperEl: HTMLElement | null = null;
const setIntroWrapperEl = (el: Element | ComponentPublicInstance | null) => {
  introWrapperEl = el instanceof HTMLElement ? el : null;
};
watch(
  showingIntro,
  (isShowing) => {
    if (isShowing) introWrapperEl?.focus();
  },
  { flush: "post" }
);

// Marks a guide step seen server-side the first time it's shown in this run — never for a
// dismissible preview (opened from the dev menu), which must never write real seen-state.
let lastMarkedSeenIndex = -1;
const markStepSeen = (step: TourStepEntity, index: number) => {
  if (index === lastMarkedSeenIndex) return;
  lastMarkedSeenIndex = index;
  // Deck-local steps (no flow guide id) have nothing to mark.
  if (!step.id) return;
  if (props.dismissible || step.alreadySeen) return;
  updateFlowGuideSeenStatus(step.id).catch((error: unknown) => Sentry.captureException(error));
};

// The last card actually shown, sent up with the finish event so a real run's completion or
// drop-off point is logged alongside it — null until something has been shown.
let stepReached: WelcomeStepReached | null = null;

// Tags the current step's own anchor with an attribute rather than a class (see style.css's
// [data-welcome-tour-anchor] rule): a held-open menu's own :class binding re-renders while its
// button is the anchor (e.g. AutoMappingWrapper's `{ 'bg-slate-100 ring-slate-300': isOpen }`), and
// Vue's class patch silently wiped a plain classList-applied class out from under v-onboarding.
let currentAnchorElement: HTMLElement | null = null;
const setCurrentAnchorElement = (element: HTMLElement | null) => {
  currentAnchorElement?.removeAttribute("data-welcome-tour-anchor");
  currentAnchorElement = element;
  currentAnchorElement?.setAttribute("data-welcome-tour-anchor", "");
};

const checkIfElementIsFound = (step: TourStepEntity | undefined, index: number) => {
  // @vue:updated fires on VOnboardingStep's own vnode whenever it patches, including the update
  // where it decides to render nothing (its injected step is still undefined, e.g. before start()
  // is called while the intro is showing) — not only once real step content is showing.
  if (!step) return;
  const element = document.getElementById(step.attachTo.element.replace("#", ""));
  if (!element) {
    // goToStep operates on the rendered (filtered) list, not the unfiltered deck.
    if (steps.value[index + 1]) goToStep(index + 1);
    else finishTour("completed");
    return;
  }
  repositionStepCard(index);
  focusCard(index);
  markStepSeen(step, index);
  stepReached = { stepIndex: displayIndexFor(index) + 1, stepKey: step.key };
  welcomeFlowStore.holdMenuOpen(step.holdsOpenMenu ?? null);
  setCurrentAnchorElement(step.highlight ? element : null);
};

let previouslyFocusedElement: Element | null = null;
let finished = false;

// Shared by the finish path and the defensive unmount path; only finishTour emits.
const releasePage = () => {
  document.querySelector("#app")?.removeAttribute("inert");
  setCurrentAnchorElement(null);
  coordinationStore.unregisterActiveTour(closeFromOutside);
  welcomeFlowStore.holdMenuOpen(null);
  if (previouslyFocusedElement instanceof HTMLElement) previouslyFocusedElement.focus();
};

const closeFromOutside = () => finishTour("skipped");

const finishTour = (result: "completed" | "skipped") => {
  if (finished) return;
  finished = true;
  try {
    finish();
  } finally {
    releasePage();
  }
  emit("finished", result, stepReached);
};

// Browser back/forward (or any other navigation while a tour is open) leaves the tour anchored to a
// page that's no longer there. finishTour's own `finished` guard keeps this from double-firing after
// the deck's own CTA has already finished the tour and pushed a route itself (e.g. the project deck's
// "Set up your project"). Optional chain despite useRoute()'s non-nullable type: dev-menu previews and
// stories can run with no router installed, where it resolves to undefined at runtime.
watch(
  () => route?.fullPath,
  () => finishTour("skipped")
);

const startAnchoredSteps = async () => {
  showingIntro.value = false;

  if (steps.value.length === 0) {
    steps.value = buildAnchoredSteps(await fetchGuideSteps());
  }

  // Never lock the page with nothing to show: if the guide has no rendered steps, the intro's
  // Next simply finishes the tour.
  if (steps.value.length === 0) {
    finishTour("completed");
    return;
  }

  start();
};

const handleBack = (isFirst: boolean, previous: () => void) => {
  if (!isFirst) {
    previous();
    return;
  }
  // Back on the anchored deck's own first step returns to the intro instead of going nowhere.
  // finish() clears v-onboarding's step so the intro's Next (start()) resets cleanly to step 0.
  if (introWelcomeStep) {
    finish();
    showingIntro.value = true;
    welcomeFlowStore.holdMenuOpen(null);
    stepReached = { stepIndex: 1, stepKey: "intro" };
  }
};

const handlePrimary = (step: TourStepEntity, isLast: boolean, next: () => void) => {
  if (!isLast) {
    next();
    return;
  }
  finishTour("completed");
  if (step.cta) {
    // runCta may return void or a promise, and may throw synchronously: the chain catches both.
    Promise.resolve()
      .then(() =>
        props.deck.ending.runCta({
          router,
          projectId: typeof route.params.id === "string" ? route.params.id : undefined,
        })
      )
      .catch((error: unknown) => Sentry.captureException(error));
  }
};

const handleSkip = () => finishTour("skipped");

const handleEscape = (event: KeyboardEvent) => {
  if (event.key === "Escape") finishTour("skipped");
};

onMounted(async () => {
  void fetchGuideSteps(); // kick off in parallel — awaited by startAnchoredSteps, not blocking the intro

  try {
    await preloadIntroContent();

    if (introWelcomeStep) {
      showingIntro.value = true;
      stepReached = { stepIndex: 1, stepKey: "intro" };
    } else {
      await startAnchoredSteps();
    }

    // Inert last, only once something is on screen.
    document.querySelector("#app")?.setAttribute("inert", "");
  } catch (error) {
    Sentry.captureException(error);
    // Unmount rather than leaving the page stuck: nothing rendered, but #app would still be locked.
    finishTour("skipped");
    return;
  }

  previouslyFocusedElement = document.activeElement;
  // End a flow guide that may already be running, then take the slot as the welcome tour.
  coordinationStore.closeActiveTour();
  coordinationStore.registerActiveTour(closeFromOutside, "welcome");
  if (canDismiss.value) window.addEventListener("keydown", handleEscape);
});

onUnmounted(() => {
  if (canDismiss.value) window.removeEventListener("keydown", handleEscape);
  if (!finished) releasePage();
});
</script>

<template>
  <Teleport to="body">
    <div class="welcome-tour">
      <!-- Large centered intro (first step of both decks): a fixed dimmed layer, no anchor, no cut-out. -->
      <div
        v-if="introWelcomeStep && showingIntro"
        class="z-tour bg-scrim/50 fixed inset-0 flex items-center justify-center p-4">
        <WelcomeConfetti v-if="deck.celebrate" />
        <div
          :ref="setIntroWrapperEl"
          class="relative"
          tabindex="-1"
          role="dialog"
          aria-modal="true"
          aria-labelledby="welcome-tour-intro-heading"
          @keydown.right="startAnchoredSteps">
          <WelcomeTourCard
            :skippable="showsSkip"
            size="lg"
            :title="introContent.title"
            :body-html="introContent.bodyHtml"
            :step-index="0"
            :step-count="totalStepCount"
            primary-label="Next"
            heading-id="welcome-tour-intro-heading"
            @next="startAnchoredSteps"
            @skip="handleSkip" />
        </div>
        <div
          class="sr-only"
          role="status"
          aria-live="polite">
          Step 1 of {{ totalStepCount }}: {{ introContent.title }}
        </div>
      </div>

      <VOnboardingWrapper
        ref="onboardingWrapper"
        :steps="steps"
        :options="onboardingOptions">
        <template #default="{ previous, next, step, isFirst, isLast, index }">
          <!-- Keyed by index so the step node remounts on every step change — stale Popper
               instances otherwise fight over a shared card. -->
          <VOnboardingStep
            :key="index"
            @vue:updated="checkIfElementIsFound(step, index)">
            <div
              :ref="setCardWrapperEl"
              tabindex="-1"
              role="dialog"
              aria-modal="true"
              :aria-labelledby="`welcome-tour-heading-${index}`"
              @keydown.left="handleBack(isFirst, previous)"
              @keydown.right="!isLast && next()">
              <Transition name="fade-in-quick">
                <WelcomeTourCard
                  :key="index"
                  :skippable="showsSkip"
                  :title="step.title"
                  :body-html="step.bodyHtml"
                  :step-index="displayIndexFor(index)"
                  :step-count="totalStepCount"
                  :primary-label="isLast ? step.cta?.label ?? 'Finish' : 'Next'"
                  :heading-id="`welcome-tour-heading-${index}`"
                  @back="handleBack(isFirst, previous)"
                  @next="handlePrimary(step, isLast, next)"
                  @skip="handleSkip" />
              </Transition>
            </div>
            <!-- VOnboardingStep's own render gates its default slot on its injected `step` being
                 truthy (see the useVOnboarding source), so anything reading `step` must live INSIDE
                 it, not as a sibling — a sibling renders unconditionally, before start() has ever
                 run, and crashes reading `step.key` off the still-undefined initial step. -->
            <div
              class="sr-only"
              role="status"
              aria-live="polite">
              Step {{ displayIndexFor(index) + 1 }} of {{ totalStepCount }}: {{ step.title }}
            </div>
          </VOnboardingStep>
        </template>
      </VOnboardingWrapper>
    </div>
  </Teleport>
</template>

<style scoped>
/* The cut-out dim matches the intro layer's backdrop (bg-scrim/50) so both read as one overlay. */
.welcome-tour {
  --v-onboarding-step-z: var(--z-index-tour);
  --v-onboarding-overlay-opacity: 0.5;
  --v-onboarding-overlay-fill: var(--color-scrim);
}
</style>
